import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { ExportDataButton } from "components/ExportDataButton";
import { Importer } from "components/Importer";
import { Tenant } from "components/TenantPicker";
import { TenantPickerDialog } from "components/TenantPickerDialog";
import { RegistrationDetailDrawer } from "components/waste/registration/RegistrationDetailDrawer";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { gql } from "generated-graphql";
import {
  StateWasteStreamRegistrationsQuery,
  StateWasteStreamRegistrationsQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchCommandButtonsContext, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { STATE_WASTE_STREAM_REGISTRATIONS_QUERY } from "./api";
import { COLUMNS, EXCLUDE_FILTER_COLUMNS, RegistrationRow } from "./columns";
import { CreateRegistrationDialog } from "./CreateRegistrationDialog";

// Resolves the selected tenant's display name from the id carried in the URL,
// so the header/export label survive a refresh or shared link without stashing
// the whole tenant object. Unique operation name to avoid colliding with the
// identical GetTenantName query elsewhere.
export const STATE_WASTE_STREAMS_TENANT_NAME_QUERY = gql(`
  query StateWasteStreamsTenantName($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      name
    }
  }
`);

// Stable, module-level grid config (V2 requires stable references for its
// perf model).
function getItems(data: StateWasteStreamRegistrationsQuery): RegistrationRow[] {
  return data.stateWasteStreamRegistrations?.items ?? [];
}

function getCount(data: StateWasteStreamRegistrationsQuery): number {
  return data.stateWasteStreamRegistrations?.count ?? 0;
}

function getRowId(row: RegistrationRow): string {
  return row.id;
}

// Merges the page-level tenant into the omnisearch search string so the
// resolver (which already parses a `tenantId:` key) and the export (which
// reads this same merged string via `commandButtons`) both scope to exactly
// the selected tenant. Replaces any existing tenantId term to ensure only the
// page's tenant is scoped.
export function withTenant(search: string, tenantId: string): string {
  // Remove any existing tenantId:... term(s) from the search string.
  // The tenantId term format is: tenantId:<value> where value may be comma/pipe-separated
  // and may be quoted, e.g. tenantId:a,b or tenantId:"value" or tenantId:a|b|c
  const tenantIdRemoved = search.replace(
    /\btenantId:(?:"[^"]*"|[^,|\s"]+)(?:\s*[,|]\s*(?:"[^"]*"|[^,|\s"]+))*/g,
    ""
  );

  // Collapse double spaces and trim
  const cleaned = tenantIdRemoved.replace(/\s+/g, " ").trim();

  // Append the page's tenantId
  return cleaned ? `${cleaned} tenantId:${tenantId}` : `tenantId:${tenantId}`;
}

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "state", sort: "asc" }];

// noLinkedStream is filter-only: the "No linked Encamp stream" filter needs the
// column defined (filters are derived from `columns`), but the Linked Streams
// count already conveys the same thing, so the column itself stays hidden.
const COLUMN_VISIBILITY_MODEL = { noLinkedStream: false };

export const StateWasteStreams = () => {
  useBreadcrumb({ label: "State Waste Streams" });

  const {
    selectedId: registrationId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("registrationId");

  // The selected tenant lives in the URL (shareable, deep-linkable) instead of
  // persisted local state, so the page defaults to no org until one is picked.
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get("tenantId") ?? "";

  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: tenantData } = useQuery(STATE_WASTE_STREAMS_TENANT_NAME_QUERY, {
    variables: { tenantId },
    skip: !tenantId,
  });
  const tenantName = tenantData?.tenant.name ?? "";
  const organizationLabel = tenantId ? tenantName || tenantId : "None selected";

  const selectTenant = useCallback(
    (t: Tenant) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tenantId", t.id);
        return next;
      });
      setPickerOpen(false);
    },
    [setSearchParams]
  );

  const buildVariables = useCallback(
    (
      search: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): StateWasteStreamRegistrationsQueryVariables => ({
      search: withTenant(search, tenantId),
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    [tenantId]
  );

  const onRowClick = useCallback(
    (params: GridRowParams<RegistrationRow>) => pushIdToUrl(params.row.id),
    [pushIdToUrl]
  );

  const commandButtons = useCallback(
    ({ search }: OmnisearchCommandButtonsContext) => [
      <Importer
        key="import"
        templateType={OneSchemaTemplateType.StateWasteStreamRegistration}
        tenantId={tenantId}
        label="Import"
        refetchQueries={["StateWasteStreamRegistrations"]}
      />,
      <ExportDataButton
        key="export"
        tenantId={tenantId}
        exportType="stateWasteStreamRegistrations"
        fileName={`${tenantName || tenantId}-state-waste-stream-registrations`}
        filter={{ search }}
        timeoutInSeconds={900}
      />,
      <Button
        key="create-registration"
        variant="outlined"
        startIcon={<Add />}
        onClick={() => setCreateOpen(true)}
      >
        Create registration
      </Button>,
    ],
    [tenantId, tenantName]
  );

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 3, pt: 3 }}>
        <Typography variant="body1">
          Organization: <strong>{organizationLabel}</strong>
        </Typography>
        <Button variant="outlined" onClick={() => setPickerOpen(true)}>
          {tenantId ? "Change" : "Select tenant"}
        </Button>
      </Stack>

      {tenantId ? (
        <OmnisearchDataGridV2<
          StateWasteStreamRegistrationsQuery,
          StateWasteStreamRegistrationsQueryVariables,
          RegistrationRow
        >
          query={STATE_WASTE_STREAM_REGISTRATIONS_QUERY}
          persistenceKey="staff-state-waste-stream-registrations"
          tenantId={tenantId}
          columns={COLUMNS}
          getItems={getItems}
          getCount={getCount}
          getRowId={getRowId}
          buildVariables={buildVariables}
          initialPageSize={25}
          initialSortModel={INITIAL_SORT_MODEL}
          initialColumnVisibilityModel={COLUMN_VISIBILITY_MODEL}
          excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
          noRowsMessage="No state waste stream registrations found."
          commandButtons={commandButtons}
          onRowClick={onRowClick}
          sx={{ pt: 2 }}
        />
      ) : (
        <Box sx={{ px: 3, pt: 6, textAlign: "center" }}>
          <Typography color="text.secondary">
            Select a tenant to view its state waste stream registrations.
          </Typography>
        </Box>
      )}

      <TenantPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onTenantSelected={selectTenant}
      />

      <RegistrationDetailDrawer
        registrationId={registrationId ?? null}
        onClose={popIdFromUrl}
        refetchQueries={["StateWasteStreamRegistrations"]}
        showTenantContext
      />
      <CreateRegistrationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        tenantId={tenantId}
      />
    </>
  );
};
